import type { StaticEntityType } from "@config";
import { useViewContext } from "@editor-providers";
import { useAnchorTrigger } from "@library/shared";
import classNames from "classnames";
import { forwardRef, type ForwardedRef } from "react";
import { ElementStyled } from "../styles/Element.styled";
import type { EditorDataProps, EditorSlotsProps, ElementProps } from "../types";

export const ElementView = forwardRef(
  (
    {
      entity,
      children,
      editorData,
      editorSlots,
    }: {
      entity: StaticEntityType;
      children: React.ReactNode;
      editorData?: EditorDataProps;
      editorSlots?: EditorSlotsProps;
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { settings, id } = entity as StaticEntityType<
      ElementProps & { isFirst: boolean }
    >;
    const { view } = useViewContext();
    const isEditor = view === "editor";
    const { handleAnchorClick } = useAnchorTrigger();
    const DND_STYLE = editorData?.dndProps?.style || {};
    const DND_ATTRIBUTES = editorData?.dndProps?.attributes || {};
    const DND_POSITION = editorData?.dndProps?.insertPosition;
    const DND_IS_DRAGGING = editorData?.dndProps?.isDragging;
    const DND_IS_SORTING = editorData?.dndProps?.isSorting;
    const DND_LISTENERS = editorData?.dndProps?.listeners || {};

    return (
      <div
        ref={ref}
        {...DATA_ATTRIBUTES({ settings })}
        {...EDITOR_ATTRIBUTES({ id, isEditor, active: editorData?.isActive })}
        {...DND_ATTRIBUTES}
        {...DND_LISTENERS}
        style={DND_STYLE}
        className={classNames(
          "elementContainer tw-relative tw-mb-element-gap-bottom tw-h-auto tw-w-full tw-outline-none [align-items:inherit] [justify-content:inherit] [text-align:inherit] last-of-type:tw-mb-0",
          {
            "--active": editorData?.isActive,
            "--active-thread": editorData?.isActiveThread,
            "--is-dragging": DND_IS_DRAGGING,
            "--classification":
              editorData?.hasClassificationSet && editorData?.admin,
            "--dynamic-variable": editorData?.hasDynamicVariable,
          },
        )}
      >
        <ElementStyled
          id={id}
          className="element-wrapper-XNCgh1eTFF elementWrapper tw-relative tw-z-1 tw-w-full [align-items:inherit] [justify-content:inherit] [text-align:inherit]"
          {...STYLED_ATTRIBUTES({
            settings,
            isSorting: !!DND_IS_SORTING,
            insertPosition: DND_POSITION,
          })}
          onClick={(e) =>
            isEditor
              ? e.preventDefault()
              : handleAnchorClick(e, settings.selectedLinkType)
          }
        >
          {editorSlots?.actionsSlot}
          {editorSlots?.entityThreadLabel}
          {editorSlots?.coeditingEntityOutline}
          {children}
        </ElementStyled>
      </div>
    );
  },
);

//  ======= ATTRIBUTE CREATION =======
const STYLED_ATTRIBUTES = ({
  settings,
  isSorting,
  insertPosition,
}: {
  settings: ElementProps;
  isSorting: boolean;
  insertPosition?: "before" | "after" | undefined;
}) => ({
  $horizontalAlignment: settings.horizontalAlignment,
  $isAnchorActive: settings.isAnchorActive,
  $selectedLinkType: settings.selectedLinkType,
  ...(insertPosition != null && { $insertPosition: insertPosition }),
  $isSorting: isSorting,
});

const DATA_ATTRIBUTES = ({ settings }: { settings: ElementProps }) => {
  const { isAnchorActive, anchorName, anchorTrigger, name } = settings;
  return {
    "data-anchor-target": isAnchorActive ? anchorName : "",
    "data-anchor-trigger": name === "Button" ? "" : anchorTrigger,
    "data-element-name": name,
    ...getDynamicAttributes({ settings }),
  };
};
//  ======= ATTRIBUTE CREATION =======

const getDynamicAttributes = ({ settings }: { settings: any }) => {
  const attributes: Record<string, string> = {};
  if (
    !window.location.href.includes("printToPdf=true") ||
    !settings.anchorTrigger
  )
    return attributes;
  attributes["as"] = settings.name === "Button" ? "div" : "a";

  if (attributes["as"] === "a") {
    attributes["href"] = `${window.location.href}#${
      settings.name === "Button" ? undefined : settings.anchorTrigger
    }`;
  }

  return attributes;
};

const EDITOR_ATTRIBUTES = ({
  id,
  isEditor = false,
  active = false,
}: {
  id: string;
  isEditor: boolean;
  active?: boolean | undefined;
}) => {
  const payload: Record<string, string | number> = {
    "data-layer-id": id,
    "data-layer-type": "element",
  };
  if (active && isEditor) payload["tabIndex"] = 0;
  return payload;
};
